import React, { memo, useCallback } from 'react'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import { DataRow } from 'src/components/shared/DataRow'
import { ContractorDetails } from 'src/interfaces/contractor'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UpdatePartnerDriver, UpdateSubcontractorExternalId } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { ContractorOnboardingStatusChip } from '../ContractorOnboardingStatusChip'
import { getContractorStatus } from 'src/utils/contractor'
import { OnboardingStatus } from 'src/constants/onboarding/enums'
import { ContractorTypeChip } from 'src/components/contractor-list/shared/ContractorTypeChip'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface Props {
  contractorDetails: ContractorDetails
  onboardingStatus: OnboardingStatus
  onboardingFailedAt?: string | null
  onboardingFailedSteps?: string[]
  onboardingExpiredSteps?: string[]
  isSubcontractor?: boolean
  isRequestApprove?: boolean
  onUpdate(): void | Promise<void>
}

const ContractorPartnerDriverInfoComponent = ({
  contractorDetails,
  onboardingStatus,
  onboardingFailedAt,
  onboardingFailedSteps,
  onboardingExpiredSteps,
  isSubcontractor,
  isRequestApprove,
  onUpdate,
}: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const canEditPartnerDriver = hasPermission(UserPermission.PartnerDriversEdit)
  const canEditExternalId = !isRequestApprove && (isSubcontractor || canEditPartnerDriver)
  const canEditEmail = !isRequestApprove && canEditPartnerDriver

  const handleEditExternalId = useCallback(
    async (externalId: string) => {
      if (externalId === contractorDetails.externalId) {
        return
      }

      try {
        if (isSubcontractor) {
          await createAsyncAction(
            dispatch,
            UpdateSubcontractorExternalId.request({
              partnerDriverId: contractorDetails.id,
              externalId,
            }),
          )
        } else {
          await createAsyncAction(
            dispatch,
            UpdatePartnerDriver.request({
              partnerDriverId: contractorDetails.id,
              externalId,
            }),
          )
        }

        await onUpdate()
        showToast('Contractor ID saved.')
      } catch (err: any) {
        handleApiError(err)
      }
    },
    [contractorDetails, onUpdate, isSubcontractor],
  )

  const handleEditEmail = useCallback(
    async (email: string) => {
      if (email === contractorDetails.email) {
        return
      }

      try {
        await createAsyncAction(
          dispatch,
          UpdatePartnerDriver.request({
            partnerDriverId: contractorDetails.id,
            email,
          }),
        )

        await onUpdate()
        showToast('Email updated.', { variant: 'success' })
      } catch (err: any) {
        handleApiError(err)
      }
    },
    [contractorDetails, onUpdate],
  )

  return (
    <div css={tw`flex p-4`}>
      <div css={tw`w-full`}>
        <div>
          <Typography variant="h2">{`${contractorDetails.firstName} ${contractorDetails.lastName}`}</Typography>
          {contractorDetails.id && <DataRow className={tw`pt-1`} title={contractorDetails.id} />}
        </div>
        <DataRow
          title="Contractor status"
          direction="row"
          component={
            <ContractorOnboardingStatusChip
              contractorStatus={getContractorStatus(contractorDetails)}
              deactivationReason={contractorDetails.deactivationReason}
              deactivatedAt={contractorDetails.deactivatedAt}
              deactivatedByEmail={contractorDetails.deactivatedByEmail}
              onboardingStatus={onboardingStatus}
              failedAt={onboardingFailedAt}
              failedSteps={onboardingFailedSteps}
              expiredSteps={onboardingExpiredSteps}
              onboardedAt={contractorDetails.onboardedAt}
            />
          }
        />
        <DataRow
          title="Contractor type"
          direction="row"
          component={<ContractorTypeChip type={contractorDetails.contractorType} />}
        />
        <DataRow
          title="External ID"
          text={contractorDetails.externalId}
          value={contractorDetails.externalId}
          onEdit={canEditExternalId ? handleEditExternalId : undefined}
          showCopyButton
        />
        {contractorDetails?.businessInfo?.businessName && (
          <DataRow
            direction="column"
            title="Business name"
            text={contractorDetails.businessInfo.businessName}
          />
        )}
        {contractorDetails.phone && (
          <DataRow direction="row" title="Phone number" text={contractorDetails.phone} />
        )}
        {contractorDetails.email && (
          <DataRow
            direction="row"
            title="Email"
            editFiledType="email"
            text={contractorDetails.email}
            value={contractorDetails.email}
            editPlaceholder="example@gigsafe.com"
            editWarningTitle="Heads up"
            editWarningSubtitle="If the Yardstik check has already started, the report will go to the old email. Once it's done, the contractor can also view it on the Contractor Portal."
            onEdit={canEditEmail ? handleEditEmail : undefined}
          />
        )}
        {contractorDetails.masterContractorName && (
          <DataRow
            direction="row"
            title="Master contractor"
            text={contractorDetails.masterContractorName}
          />
        )}
      </div>
    </div>
  )
}

export const ContractorPartnerDriverInfo = memo(ContractorPartnerDriverInfoComponent)
